import React, { useState } from 'react';
import { useUserProfile } from '../../hooks/useUserProfile';
import { UpdateUserDto, ChangePasswordDto } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { LogOut, AlertTriangle } from 'lucide-react';
import { Dialog } from '../../components/Dialog';
import './UserProfileEdit.css';

interface UserProfileEditProps {
  userId: string;
  onSuccess?: () => void;
}

export const UserProfileEdit: React.FC<UserProfileEditProps> = ({ userId, onSuccess }) => {
  const { user, loading, error, updating, changingPassword, updateProfile, changePassword } = useUserProfile(userId);
  const { logout } = useAuth();
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  // Estado para el formulario de perfil
  const [profileForm, setProfileForm] = useState({
    fullName: '',
    email: '',
  });

  // Estado para el formulario de contraseña
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Inicializar formulario cuando se carga el usuario
  React.useEffect(() => {
    if (user) {
      setProfileForm({
        fullName: user.fullName || '',
        email: user.email || '',
      });
    }
  }, [user]);

  // Manejar cambios en el formulario de perfil
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({ ...prev, [name]: value }));
  };

  // Manejar cambios en el formulario de contraseña
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
    setPasswordError(null);
  };

  // Guardar cambios de perfil
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);

    const updateData: UpdateUserDto = {
      fullName: profileForm.fullName,
      email: profileForm.email,
      role: user?.role || null, // Keep existing role
    };

    const success = await updateProfile(updateData);
    if (success) {
      setSuccessMessage('Perfil actualizado exitosamente');
      onSuccess?.();
    }
  };

  // Cambiar contraseña
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setPasswordError(null);

    // Validar que las contraseñas coincidan
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }

    // Validar longitud mínima
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    const passwordData: ChangePasswordDto = {
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    };

    const success = await changePassword(passwordData);
    if (success) {
      setSuccessMessage('Contraseña cambiada exitosamente');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      onSuccess?.();
    }
  };

  const handleLogoutConfirm = () => {
    logout();
    setIsLogoutConfirmOpen(false);
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
        <p>Cargando perfil...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-error">
        <p>No se pudo cargar el perfil del usuario</p>
      </div>
    );
  }

  return (
    <div className="user-profile-edit">
      <h1>Editar Perfil</h1>

      {/* Mensajes de éxito y error */}
      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* Información del usuario (solo lectura) */}
      <section className="profile-section">
        <h2>Información de la Cuenta</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Usuario:</label>
            <span>{user.username}</span>
          </div>
          <div className="info-item">
            <label>Rol:</label>
            <span className="badge">{user.role}</span>
          </div>
          <div className="info-item">
            <label>Estado:</label>
            <span className={`status ${user.isActive ? 'active' : 'inactive'}`}>
              {user.isActive ? 'Activo' : 'Inactivo'}
            </span>
          </div>
          <div className="info-item">
            <label>Fecha de registro:</label>
            <span>{new Date(user.createdAt).toLocaleDateString()}</span>
          </div>
          {user.lastLoginAt && (
            <div className="info-item">
              <label>Último acceso:</label>
              <span>{new Date(user.lastLoginAt).toLocaleString()}</span>
            </div>
          )}
        </div>
      </section>

      {/* Formulario de edición de perfil */}
      <section className="profile-section">
        <h2>Datos Personales</h2>
        <form onSubmit={handleProfileSubmit} className="profile-form">
          <div className="form-group">
            <label htmlFor="fullName">Nombre Completo *</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={profileForm.fullName}
              onChange={handleProfileChange}
              required
              disabled={updating}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Correo Electrónico *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={profileForm.email}
              onChange={handleProfileChange}
              required
              disabled={updating}
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={updating}>
              {updating ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </section>

      {/* Formulario de cambio de contraseña */}
      <section className="profile-section">
        <h2>Cambiar Contraseña</h2>
        
        {passwordError && (
          <div className="alert alert-error">
            {passwordError}
          </div>
        )}

        <form onSubmit={handlePasswordSubmit} className="profile-form">
          <div className="form-group">
            <label htmlFor="currentPassword">Contraseña Actual *</label>
            <input
              type="password"
              id="currentPassword"
              name="currentPassword"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              required
              disabled={changingPassword}
              autoComplete="current-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">Nueva Contraseña *</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              required
              disabled={changingPassword}
              autoComplete="new-password"
              minLength={6}
            />
            <small className="form-hint">Mínimo 6 caracteres</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Nueva Contraseña *</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              required
              disabled={changingPassword}
              autoComplete="new-password"
              minLength={6}
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={changingPassword}>
              {changingPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
            </button>
          </div>
        </form>
      </section>

      {/* Sección de cerrar sesión */}
      <section className="profile-section">
        <h2>Sesión</h2>
        <p className="text-slate-600 mb-4">
          Cierra tu sesión para salir de la aplicación de forma segura.
        </p>
        <button
          onClick={() => setIsLogoutConfirmOpen(true)}
          className="btn btn-danger flex items-center gap-2"
        >
          <LogOut size={18} />
          Cerrar Sesión
        </button>
      </section>

      {/* Diálogo de confirmación de logout */}
      <Dialog 
        isOpen={isLogoutConfirmOpen} 
        onClose={() => setIsLogoutConfirmOpen(false)}
        title="Cerrar Sesión"
        size="sm"
        noPadding
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle size={24} className="text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">¿Cerrar sesión?</h3>
              <p className="text-sm text-slate-600">Tendrás que volver a iniciar sesión</p>
            </div>
          </div>
          <p className="text-sm text-slate-700 mb-6">
            ¿Estás seguro de que quieres cerrar sesión? Tendrás que volver a ingresar tus credenciales para acceder.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setIsLogoutConfirmOpen(false)}
              className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleLogoutConfirm}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <LogOut size={16} />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
